/**
 * Synapse — Types partagés et schémas de validation Zod.
 *
 * Ce package est importé par TOUS les autres (web, api, engine, etc.).
 * C'est la source unique de vérité pour les structures de données.
 */
import { z } from "zod";
// ═══════════════════════════════════════════════════════════
//  ENUMS & CONSTANTES
// ═══════════════════════════════════════════════════════════
export const MissionStatus = {
    IDEA: "idea",
    PLAN: "plan",
    VALIDATION: "validation",
    DONE: "done",
};
export const ControlMode = {
    MANUAL: "manual",
    SEMI: "semi",
    AUTO: "auto",
};
export const EnvType = {
    DEV: "dev",
    STAGE: "stage",
    PROD: "prod",
};
export const CrewRole = {
    NAVIGATOR: "navigator",
    CERVELAU: "cervelleau",
    PROGRAMMEUR: "programmeur",
    SECURITE: "securite",
    CREATEUR: "createur",
};
export const LLMProvider = {
    ZAI: "zai",
    OPENAI: "openai",
    CODEX: "codex",
};
export const NotifChannel = {
    DISCORD: "discord",
    WEB: "web",
    BELL: "bell",
};
export const TraceActionType = {
    CODE_WRITE: "code_write",
    SEARCH: "search",
    TEST_RUN: "test_run",
    QUESTION: "question",
    DECISION: "decision",
};
export const TraceStatus = {
    SUCCESS: "success",
    ERROR: "error",
    RUNNING: "running",
};
export const AuditSeverity = {
    INFO: "info",
    WARN: "warn",
    ERROR: "error",
    CRITICAL: "critical",
};
export const CommanderRole = {
    ADMIN: "admin",
    OPS: "ops",
    VIEWER: "viewer",
};
// ═══════════════════════════════════════════════════════════
//  SCHEMAS — PROJECTS
// ═══════════════════════════════════════════════════════════
export const ProjectSchema = z.object({
    id: z.string().uuid(),
    name: z.string().min(1),
    color: z.string().default("#b794f6"),
    repo: z.string().optional(),
    envs: z.array(z.nativeEnum(EnvType)).default([EnvType.DEV]),
    description: z.string().optional(),
    createdAt: z.string().datetime(),
    updatedAt: z.string().datetime(),
});
// ═══════════════════════════════════════════════════════════
//  SCHEMAS — MISSIONS
// ═══════════════════════════════════════════════════════════
export const MissionSchema = z.object({
    id: z.string().uuid(),
    title: z.string().min(1),
    description: z.string().default(""),
    status: z.nativeEnum(MissionStatus).default(MissionStatus.IDEA),
    projectId: z.string().uuid(),
    kanbanOrder: z.number().nullable().default(null),
    skills: z.array(z.string()).default([]),
    controlMode: z.nativeEnum(ControlMode).default(ControlMode.SEMI),
    attempt: z.number().int().min(0).default(0),
    maxAttempts: z.number().int().min(1).default(6),
    targetAttempts: z.number().int().min(1).default(3),
    minSatisfaction: z.number().min(0).max(100).default(90),
    satisfaction: z.number().min(0).max(100).nullable().default(null),
    crewIds: z.array(z.string().uuid()).default([]),
    createdAt: z.string().datetime(),
    updatedAt: z.string().datetime(),
});
export const MissionTraceSchema = z.object({
    id: z.string().uuid(),
    missionId: z.string().uuid(),
    attemptNumber: z.number().int().min(1),
    agentId: z.string().uuid().optional(),
    agentName: z.string(),
    agentRole: z.nativeEnum(CrewRole).optional(),
    actionType: z.nativeEnum(TraceActionType),
    description: z.string(),
    status: z.nativeEnum(TraceStatus),
    artifacts: z.array(z.string()).default([]),
    errorMessage: z.string().optional(),
    durationMs: z.number().int().nullable().default(null),
    createdAt: z.string().datetime(),
});
// ═══════════════════════════════════════════════════════════
//  SCHEMAS — CREW
// ═══════════════════════════════════════════════════════════
export const CrewMemberSchema = z.object({
    id: z.string().uuid(),
    name: z.string().min(1),
    role: z.nativeEnum(CrewRole),
    description: z.string().default(""),
    avatar: z.string().default("🤖"),
    avatarColor: z.string().default("#b794f6"),
    llmProvider: z.nativeEnum(LLMProvider).default(LLMProvider.ZAI),
    llmModel: z.string().default("glm-4.7"),
    capabilities: z.array(z.string()).default([]),
    active: z.boolean().default(true),
    systemPrompt: z.string().optional(),
    createdAt: z.string().datetime(),
    updatedAt: z.string().datetime(),
});
// ═══════════════════════════════════════════════════════════
//  SCHEMAS — SCHEDULED TASKS
// ═══════════════════════════════════════════════════════════
export const ScheduledTaskSchema = z.object({
    id: z.string().uuid(),
    name: z.string().min(1),
    icon: z.string().default("⚙"),
    description: z.string().default(""),
    cron: z.string(),
    isTemplate: z.boolean().default(false),
    active: z.boolean().default(true),
    retry: z.number().int().min(0).max(10).default(3),
    notifChannels: z.array(z.nativeEnum(NotifChannel)).default([
        NotifChannel.BELL,
    ]),
    owner: z.string().default("Jonathan"),
    createdAt: z.string().datetime(),
    updatedAt: z.string().datetime(),
});
export const ScheduledRunSchema = z.object({
    id: z.string().uuid(),
    taskId: z.string().uuid(),
    startedAt: z.string().datetime(),
    finishedAt: z.string().datetime().nullable(),
    duration: z.string().optional(),
    result: z.enum(["success", "failed", "running"]).default("running"),
    retryUsed: z.number().int().min(0).default(0),
    errorMessage: z.string().optional(),
});
// ═══════════════════════════════════════════════════════════
//  SCHEMAS — AUDIT LOG
// ═══════════════════════════════════════════════════════════
export const AuditLogSchema = z.object({
    id: z.string().uuid(),
    traceId: z.string(),
    timestamp: z.string().datetime(),
    origin: z.string().default("synapse"),
    actor: z.string(),
    actorType: z.enum(["commander", "agent", "system"]).default("system"),
    category: z.string(),
    action: z.string(),
    resource: z.string().optional(),
    severity: z.nativeEnum(AuditSeverity).default(AuditSeverity.INFO),
    ip: z.string().optional(),
    metadata: z.record(z.unknown()).optional(),
});
// ═══════════════════════════════════════════════════════════
//  SCHEMAS — NOTIFICATIONS
// ═══════════════════════════════════════════════════════════
export const NotificationSchema = z.object({
    id: z.string().uuid(),
    type: z.enum(["mission", "cicd", "security", "system", "approval"]),
    severity: z.enum(["info", "warn", "error", "success"]),
    title: z.string(),
    message: z.string(),
    channel: z.nativeEnum(NotifChannel).default(NotifChannel.BELL),
    read: z.boolean().default(false),
    createdAt: z.string().datetime(),
    linkId: z.string().uuid().optional(),
});
// ═══════════════════════════════════════════════════════════
//  SCHEMAS — COMMANDERS
// ═══════════════════════════════════════════════════════════
export const CommanderSchema = z.object({
    id: z.string().uuid(),
    email: z.string().email(),
    name: z.string(),
    role: z.nativeEnum(CommanderRole).default(CommanderRole.VIEWER),
    avatar: z.string().default("👤"),
    accessWebUI: z.boolean().default(true),
    accessDiscord: z.boolean().default(false),
    cfAccessSession: z.string().optional(),
    lastActiveAt: z.string().datetime().optional(),
    createdAt: z.string().datetime(),
});
/** Payload de création d'un commandant (gestion des accès) */
export const CreateCommanderInput = z.object({
    email: z.string().email(),
    name: z.string().min(1),
    role: z.nativeEnum(CommanderRole).default(CommanderRole.VIEWER),
    avatar: z.string().default("👤"),
    accessWebUI: z.boolean().default(true),
    accessDiscord: z.boolean().default(false),
});
/** Payload de mise à jour d'un commandant (tous champs optionnels) */
export const UpdateCommanderInput = z.object({
    email: z.string().email().optional(),
    name: z.string().min(1).optional(),
    role: z.nativeEnum(CommanderRole).optional(),
    avatar: z.string().optional(),
    accessWebUI: z.boolean().optional(),
    accessDiscord: z.boolean().optional(),
});
// ═══════════════════════════════════════════════════════════
//  SCHEMAS — CI/CD EVENTS
// ═══════════════════════════════════════════════════════════
export const CicdEventSchema = z.object({
    id: z.string().uuid(),
    traceId: z.string(),
    timestamp: z.string().datetime(),
    project: z.string(),
    pipelineType: z.enum(["build", "deploy", "test", "security_scan"]),
    status: z.enum(["running", "success", "failed", "cancelled"]),
    branch: z.string().default("main"),
    commit: z.string().optional(),
    actor: z.string().optional(),
    durationMs: z.number().int().nullable().default(null),
    artifacts: z.array(z.string()).default([]),
    logUrl: z.string().url().optional(),
});
// ═══════════════════════════════════════════════════════════
//  SCHEMAS — SETTINGS (key-value store)
// ═══════════════════════════════════════════════════════════
export const SettingSchema = z.object({
    key: z.string().min(1),
    value: z.string(),
    isSecret: z.boolean().default(false),
    updatedAt: z.string().datetime(),
});
// ═══════════════════════════════════════════════════════════
//  SCHEMAS — LLM ROUTING
// ═══════════════════════════════════════════════════════════
export const LlmRoutingSchema = z.object({
    cervelleau: z.object({
        provider: z.nativeEnum(LLMProvider).default(LLMProvider.ZAI),
        model: z.string().default("glm-4.7"),
    }),
    navigator: z.object({
        provider: z.nativeEnum(LLMProvider).default(LLMProvider.ZAI),
        model: z.string().default("glm-4.7"),
    }),
    programmeur: z.object({
        provider: z.nativeEnum(LLMProvider).default(LLMProvider.ZAI),
        model: z.string().default("glm-4.7"),
    }),
    securite: z.object({
        provider: z.nativeEnum(LLMProvider).default(LLMProvider.OPENAI),
        model: z.string().default("o3"),
    }),
    createur: z.object({
        provider: z.nativeEnum(LLMProvider).default(LLMProvider.ZAI),
        model: z.string().default("glm-4.7"),
    }),
});
// ═══════════════════════════════════════════════════════════
//  SCHEMAS — API CONTRACTS
// ═══════════════════════════════════════════════════════════
/** Payload de création d'une mission */
export const CreateMissionInput = MissionSchema.omit({
    id: true,
    createdAt: true,
    updatedAt: true,
    attempt: true,
    satisfaction: true,
}).partial({
    status: true,
    kanbanOrder: true,
    skills: true,
    controlMode: true,
    maxAttempts: true,
    targetAttempts: true,
    minSatisfaction: true,
    crewIds: true,
});
export const UpdateMissionInput = MissionSchema.partial().required({ id: true });
// ═══════════════════════════════════════════════════════════
//  SETTINGS — REGISTRE UNIQUE DES CLÉS (source de vérité)
// ═══════════════════════════════════════════════════════════
//
// Convention: `<domaine>.<fournisseur>.<code>` (ex. `llm.zai.api_key`).
// Front, API et Engine DOIVENT importer ces constantes — jamais de
// chaîne littérale en dur — pour rester alignés sur les mêmes clés DB.
export const SETTING_KEYS = {
    // LLM · Z.ai
    LLM_ZAI_API_KEY: "llm.zai.api_key",
    LLM_ZAI_ENDPOINT: "llm.zai.endpoint",
    LLM_ZAI_MODEL: "llm.zai.model",
    LLM_ZAI_MAX_TOKENS: "llm.zai.max_tokens",
    // LLM · OpenAI
    LLM_OPENAI_API_KEY: "llm.openai.api_key",
    LLM_OPENAI_MODEL: "llm.openai.model",
    LLM_OPENAI_MAX_TOKENS: "llm.openai.max_tokens",
    // LLM · OpenAI — mode d'auth (api_key | oauth) + flux OAuth 2.0 PKCE
    LLM_OPENAI_AUTH_MODE: "llm.openai.auth_mode",
    LLM_OPENAI_OAUTH_ISSUER: "llm.openai.oauth_issuer",
    LLM_OPENAI_OAUTH_CLIENT_ID: "llm.openai.oauth_client_id",
    LLM_OPENAI_OAUTH_AUTH_URL: "llm.openai.oauth_auth_url",
    LLM_OPENAI_OAUTH_TOKEN_URL: "llm.openai.oauth_token_url",
    LLM_OPENAI_OAUTH_SCOPE: "llm.openai.oauth_scope",
    LLM_OPENAI_OAUTH_REDIRECT_URI: "llm.openai.oauth_redirect_uri",
    LLM_OPENAI_OAUTH_BASE_URL: "llm.openai.oauth_base_url",
    LLM_OPENAI_OAUTH_ACCESS_TOKEN: "llm.openai.oauth_access_token",
    LLM_OPENAI_OAUTH_REFRESH_TOKEN: "llm.openai.oauth_refresh_token",
    LLM_OPENAI_OAUTH_EXPIRES_AT: "llm.openai.oauth_expires_at",
    LLM_OPENAI_OAUTH_ACCOUNT: "llm.openai.oauth_account",
    // interne/éphémère (flux PKCE en cours) — ne pas afficher dans l'UI
    LLM_OPENAI_OAUTH_PKCE_VERIFIER: "llm.openai.oauth_pkce_verifier",
    LLM_OPENAI_OAUTH_STATE: "llm.openai.oauth_state",
    // GitHub
    GITHUB_TOKEN: "github.token",
    GITHUB_REVIEW_TOKEN: "github.review_token",
    GITHUB_ORGANIZATION: "github.organization",
    GITHUB_RUNNERS: "github.runners",
    GITHUB_REGISTRY: "github.registry",
    // Discord
    DISCORD_BOT_TOKEN: "discord.bot_token",
    DISCORD_APP_ID: "discord.app_id",
    DISCORD_MISSIONS_CHANNEL: "discord.missions_channel",
    DISCORD_ALERTS_CHANNEL: "discord.alerts_channel",
    // Obsidian — vault (chemins logiques)
    OBSIDIAN_VAULT_PATH: "obsidian.vault_path",
    OBSIDIAN_MISSIONS_DIR: "obsidian.missions_dir",
    OBSIDIAN_JOURNAL_DIR: "obsidian.journal_dir",
    // Obsidian — connexion via plugin « Local REST API » (Synapse pousse en HTTPS)
    OBSIDIAN_ENABLED: "obsidian.enabled",
    OBSIDIAN_MODE: "obsidian.mode", // "local" (sidecar dans le pod) | "external" (poste distant)
    OBSIDIAN_API_URL: "obsidian.api_url",
    OBSIDIAN_API_KEY: "obsidian.api_key",
    // DRP · Wasabi
    DRP_WASABI_BUCKET: "drp.wasabi_bucket",
    DRP_WASABI_ENDPOINT: "drp.wasabi_endpoint",
    DRP_WASABI_ACCESS_KEY: "drp.wasabi_access_key",
    DRP_WASABI_SECRET_KEY: "drp.wasabi_secret_key",
    // Réseau
    NETWORK_DOMAIN: "network.domain",
    NETWORK_CORS_ORIGIN: "network.cors_origin",
    NETWORK_DB_CONNECTION: "network.db_connection",
    NETWORK_REDIS_URL: "network.redis_url",
};
/**
 * Sentinel renvoyé par l'API à la place de la valeur réelle d'un secret
 * (GET /api/settings masque les secrets). Ne JAMAIS réécrire cette valeur
 * en base — un PUT dont `value === MASKED_SECRET` doit être un no-op.
 */
export const MASKED_SECRET = "••••••••";
//# sourceMappingURL=index.js.map